const WISHLIST_STORAGE_KEY = 'kabir-wishlist';

class WishlistStorage {
  static getItems() {
    try {
      const data = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '{"items":[]}');
      return Array.isArray(data.items) ? data.items : [];
    } catch {
      return [];
    }
  }

  static saveItems(items) {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify({ items }));
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  static has(productId) {
    return this.getItems().some((item) => String(item.id) === String(productId));
  }

  static toggle(product) {
    const items = this.getItems();
    const index = items.findIndex((item) => String(item.id) === String(product.id));
    let added = false;

    if (index === -1) {
      items.unshift({
        id: String(product.id),
        handle: product.handle,
        title: product.title,
        variantId: String(product.variantId || ''),
        addedAt: Date.now(),
      });
      added = true;
    } else {
      items.splice(index, 1);
    }

    this.saveItems(items);
    return { added, items };
  }

  static remove(productId) {
    const items = this.getItems().filter((item) => String(item.id) !== String(productId));
    this.saveItems(items);
    return items;
  }
}

class WishlistDrawer {
  static getInstance() {
    if (!WishlistDrawer.instance) {
      WishlistDrawer.instance = new WishlistDrawer();
    }
    return WishlistDrawer.instance;
  }

  constructor() {
    this.drawer = document.querySelector('wishlist-drawer');
    if (!this.drawer) return;

    this.itemsContainer = this.drawer.querySelector('[data-wishlist-drawer-items]');
    this.emptyState = this.drawer.querySelector('[data-wishlist-drawer-empty]');
    this.overlay = this.drawer.querySelector('#WishlistDrawer-Overlay');
    this.closeButton = this.drawer.querySelector('.wishlist-drawer__close');
    this.continueButton = this.drawer.querySelector('[data-wishlist-continue]');
    this.addAllButton = this.drawer.querySelector('[data-wishlist-add-all]');

    this.drawer.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButton?.addEventListener('click', () => this.close());
    this.continueButton?.addEventListener('click', () => this.close());
    this.addAllButton?.addEventListener('click', () => this.addAllToCart());

    window.addEventListener('wishlist:updated', () => this.render());
    this.bindHeaderIcon();
  }

  bindHeaderIcon() {
    const icon = document.querySelector('#wishlist-icon-bubble');
    if (!icon) return;

    icon.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(icon);
    });
  }

  open(triggeredBy) {
    if (!this.drawer) return;

    if (triggeredBy) this.activeElement = triggeredBy;
    this.render();

    setTimeout(() => {
      this.drawer.classList.add('active');
    });

    document.body.classList.add('overflow-hidden');

    const focusTarget = this.drawer.querySelector('.drawer__inner');
    if (typeof trapFocus === 'function' && focusTarget) {
      trapFocus(this.drawer, focusTarget);
    }
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `Rs. ${(cents / 100).toFixed(2)}`;
  }

  getVariantLabel(product, variantId) {
    const variant = product.variants.find((entry) => String(entry.id) === String(variantId));
    if (!variant || variant.title === 'Default Title') return '';

    const sizeOptionIndex = product.options.findIndex((option) => /size/i.test(option.name));
    if (sizeOptionIndex >= 0) {
      return variant[`option${sizeOptionIndex + 1}`] || '';
    }

    return variant.title.split(' / ')[0] || variant.title;
  }

  async fetchProduct(handle) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  async render() {
    if (!this.itemsContainer || !this.emptyState) return;

    const items = WishlistStorage.getItems();

    if (!items.length) {
      this.itemsContainer.innerHTML = '';
      this.emptyState.classList.remove('hidden');
      this.addAllButton?.setAttribute('disabled', 'disabled');
      return;
    }

    this.emptyState.classList.add('hidden');
    this.addAllButton?.removeAttribute('disabled');
    this.itemsContainer.innerHTML = '';

    const products = await Promise.all(items.map((item) => this.fetchProduct(item.handle)));

    products.forEach((product, index) => {
      if (!product) return;

      const storedItem = items[index];
      const variantId = storedItem.variantId || product.variants.find((v) => v.available)?.id || product.variants[0]?.id;
      const variant = product.variants.find((entry) => String(entry.id) === String(variantId)) || product.variants[0];
      const image = variant?.featured_image?.src || product.featured_image || product.images?.[0];
      const sizeLabel = this.getVariantLabel(product, variant?.id);
      const price = this.formatMoney(variant?.price || product.price);
      const compareAt =
        variant?.compare_at_price > variant?.price ? this.formatMoney(variant.compare_at_price) : '';

      const row = document.createElement('article');
      row.className = 'wishlist-drawer__item';
      row.innerHTML = `
        <a href="${product.url}" class="wishlist-drawer__item-image">
          ${image ? `<img src="${image}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy" width="90" height="90">` : ''}
        </a>
        <div class="wishlist-drawer__item-details">
          <div class="wishlist-drawer__item-top">
            <a href="${product.url}" class="wishlist-drawer__item-title">${product.title}</a>
            <button type="button" class="wishlist-drawer__remove" data-remove-item aria-label="${window.wishlistStrings?.remove || 'Remove from wishlist'}">
              <span class="svg-wrapper">${document.querySelector('#wishlist-remove-icon-template')?.innerHTML || '×'}</span>
            </button>
          </div>
          ${sizeLabel ? `<p class="wishlist-drawer__item-variant">${sizeLabel}</p>` : ''}
          <p class="wishlist-drawer__item-price">
            ${compareAt ? `<s>${compareAt}</s>` : ''}
            <span class="wishlist-drawer__item-price-current">${price}</span>
          </p>
          <div class="wishlist-drawer__item-actions">
            <div class="wishlist-drawer__qty">
              <button type="button" class="wishlist-drawer__qty-btn" data-qty-minus aria-label="Decrease quantity">−</button>
              <span class="wishlist-drawer__qty-value" data-qty-value>1</span>
              <button type="button" class="wishlist-drawer__qty-btn" data-qty-plus aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="wishlist-drawer__add-btn" data-add-to-cart>
              ${window.wishlistStrings?.addToCart || 'Add To Cart'}
            </button>
          </div>
        </div>
      `;

      const qtyValue = row.querySelector('[data-qty-value]');
      row.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
        const current = Math.max(1, parseInt(qtyValue.textContent, 10) - 1);
        qtyValue.textContent = current;
      });
      row.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
        qtyValue.textContent = parseInt(qtyValue.textContent, 10) + 1;
      });

      row.querySelector('[data-remove-item]')?.addEventListener('click', () => {
        WishlistStorage.remove(product.id);
      });

      row.querySelector('[data-add-to-cart]')?.addEventListener('click', async () => {
        const quantity = parseInt(qtyValue.textContent, 10) || 1;
        await this.addToCart(variant?.id, quantity);
      });

      this.itemsContainer.appendChild(row);
    });
  }

  async addToCart(variantId, quantity = 1) {
    if (!variantId) return;

    await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    });

    this.close();
    document.querySelector('cart-drawer')?.open?.();
  }

  async addAllToCart() {
    const items = WishlistStorage.getItems();
    const products = await Promise.all(items.map((item) => this.fetchProduct(item.handle)));
    const cartItems = products
      .filter(Boolean)
      .map((product) => {
        const stored = items.find((item) => String(item.id) === String(product.id));
        const variantId =
          stored?.variantId || product.variants.find((variant) => variant.available)?.id || product.variants[0]?.id;
        return variantId ? { id: Number(variantId), quantity: 1 } : null;
      })
      .filter(Boolean);

    if (!cartItems.length) return;

    await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: cartItems }),
    });

    this.close();
    document.querySelector('cart-drawer')?.open?.();
  }
}

class WishlistToast {
  constructor() {
    this.toast = document.querySelector('.wishlist-toast');
    if (!this.toast) return;

    this.messageEl = this.toast.querySelector('.wishlist-toast__message');
    this.closeButton = this.toast.querySelector('.wishlist-toast__close');
    this.openButton = this.toast.querySelector('[data-wishlist-open]');
    this.hideTimeout = null;

    this.closeButton?.addEventListener('click', () => this.hide());
    this.openButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.hide();
      WishlistDrawer.getInstance().open();
    });
  }

  show({ title, added }) {
    if (!this.toast || !this.messageEl) return;

    const actionText = added ? window.wishlistStrings?.added : window.wishlistStrings?.removed;
    this.messageEl.innerHTML = `${title} has been <strong>${actionText}</strong>`;
    this.toast.classList.remove('hidden');
    clearTimeout(this.hideTimeout);
    this.hideTimeout = setTimeout(() => this.hide(), 5000);
  }

  hide() {
    this.toast?.classList.add('hidden');
    clearTimeout(this.hideTimeout);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  WishlistDrawer.getInstance();
  WishlistToast.instance = new WishlistToast();
});

if (!customElements.get('product-wishlist-button')) {
  customElements.define(
    'product-wishlist-button',
    class ProductWishlistButton extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('button');
      }

      connectedCallback() {
        this.updateState();
        this.onClickHandler = this.onClick.bind(this);
        this.button?.addEventListener('click', this.onClickHandler, true);
        this.wishlistUpdatedHandler = () => this.updateState();
        window.addEventListener('wishlist:updated', this.wishlistUpdatedHandler);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data?.variant;
            if (variant?.id) {
              this.dataset.variantId = variant.id;
            }
          });
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClickHandler, true);
        window.removeEventListener('wishlist:updated', this.wishlistUpdatedHandler);
        this.variantChangeUnsubscriber?.();
      }

      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const product = {
          id: this.dataset.productId,
          handle: this.dataset.productHandle,
          title: this.dataset.productTitle,
          variantId: this.dataset.variantId,
        };

        const { added } = WishlistStorage.toggle(product);
        this.updateState();

        const toast = WishlistToast.instance || new WishlistToast();
        WishlistToast.instance = toast;
        toast.show({ title: product.title, added });
      }

      updateState() {
        const isActive = WishlistStorage.has(this.dataset.productId);
        this.button?.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        this.button?.setAttribute(
          'aria-label',
          isActive ? window.wishlistStrings?.remove : window.wishlistStrings?.add
        );
        this.classList.toggle('is-active', isActive);
      }
    }
  );
}

if (!customElements.get('wishlist-drawer')) {
  customElements.define('wishlist-drawer', class WishlistDrawerElement extends HTMLElement {});
}
